import {
  useState,
  useEffect,
  RefObject,
  useRef,
  useMemo,
  MutableRefObject,
} from 'react';
// 组件
import { ProImage, ProTooltip } from '../../antd';
import Images from '../../Images';
import ButtonGroup from '../../ButtonGroup';
import PopoverTable from '../../PopoverTable';
import EditSelect from '../../EditSelect';
import GoodInfoColumn from '../../GoodInfoColumn';
import EdiTable from '../subpage/ediTable';
import CopyText from '../../CopyText';
import RouteLine from '../../RouteLink';
import DataStatus from '../../DataStatus';
import SkuInfoList from '../../SkuInfoList';
// 方法
import { useLayoutContext } from '../../PageLayout/context/LayoutContext';
import { renderEnumNames } from '../../utils/renderUtils';
import { moneyFormat, formatAssociateParams, formatGroupFields, getColumnValue, } from '../../utils/dataUtils';
import { filterEmptyValue, filterObjEmptyValue } from '../../utils/utils';
// 常量
import { PaginationProps } from 'antd/lib/pagination';
import { IBtnList } from '../../ButtonGroup/buttonGroup';
import { SuperTableProps } from '../index';
import { columnsProps, operationType, CommonlyTypeEnum, ListParamsProps } from '../interface';
import { TableTotalActionType } from '../subpage/tableTotal';
import {
  SUCCESS_STATUS_CODE,
  TABLE_COLUMN_EMPTY,
  TIME_ZONE_OPTIONS,
} from '../../constants/constant';
import skuInfoListConfig from '../../constants/skuInfoListConfig';
import { getDefauleSearchParams, getRowKey, setColumnTitle } from '../utils';
import { FormInstance } from 'antd/lib/form';
import { IObject } from '../../constants/interface';
import { ResizeCallbackData } from 'react-resizable';
import classNames from 'classnames';

export const initPagination: PaginationProps = {
  current: 1,
  pageSize: 20,
  showSizeChanger: true,
  pageSizeOptions: ['20', '50', '100', '200'],
  size: 'small',
};
export interface paramsType {
  [key: string]: any;
}

function useTableHook (
  props: SuperTableProps & {
    searchFormRef: RefObject<FormInstance>;
    totalRef: MutableRefObject<TableTotalActionType>;
    tableRef: MutableRefObject<any>;
  },
) {
  const {
    listApi,
    columns,
    dataKey,
    totalKey,
    operation,
    pagination,
    defauleSearchParams,
    delay,
    formItems,
    dataSource,
    operationFooterList,
    rowKey,
    actionRef,
    isMainSubTable,
    associateSelectNames,
    autoPage,
    totalRef,
    dateType,
    hideContainer,
    groupFields,
    tableRef,
    resizable,
    minVirtualTotal,
    isMergeTable,
    postData,
    onBefore,
    onClickOperation,
    onFetchComplete,
    onEditSuccess,
    onSearchChange,
    onPaginationChange,
    onBeforeRefresh,
    onRefresh,
    onReset,
    onFinal,
  } = props;
  const { enumData } = useLayoutContext();
  // 表格loading状态
  const [loading, setLoading] = useState<boolean>(false);
  // 表格总条数
  const [listTotal, setListTotal] = useState<number>(1);
  // 表格数据
  const [currentDataSource, setCurrentDataSource] = useState<any[]>(
    dataSource || [],
  );
  // 表格搜索参数
  const [listParams, setListParams] = useState<paramsType>({});
  // 分页参数
  const [paginationParams, setPaginationParams] = useState<
    PaginationProps | false
  >(pagination === false ? false : { ...initPagination, ...pagination });
  const fetchTimer = useRef<any>();
  const allDataSource = useRef<any[]>([]);
  const defaultParamsRef = useRef();
  const fetchCount = useRef<number>(0);
  let allDefauleSearchParams = useMemo(() => {
    return getDefauleSearchParams(defauleSearchParams, formItems);
  }, [defauleSearchParams, formItems]);
  useEffect(() => {
    if (autoPage && paginationParams) {
      const { current = 1, pageSize = 20 } = paginationParams;
      allDataSource.current = dataSource || [];
      const startNum = pageSize * (current - 1);
      const endNum = pageSize * current;
      if(startNum < (dataSource?.length??0)){
        setCurrentDataSource(dataSource?.slice?.(startNum, endNum) || []);
      }else{
        setCurrentDataSource(dataSource?.slice?.() || []);
      }
      if (!listApi) setListTotal(dataSource?.length || 0);
    } else {
      setListTotal(dataSource?.length || 0);
      setCurrentDataSource(dataSource?.slice?.() || []);
    }
  }, [dataSource]);
  useEffect(() => {
    if (!listApi) return;
    const params =
      pagination === false ? false : { ...initPagination, ...paginationParams, current: 1, };
    setPaginationParams(params);
    handleScrollTop();
    fetch({...allDefauleSearchParams, ...listParams, ...defauleSearchParams}, params, true);
    defaultParamsRef.current = defauleSearchParams;
  }, [defauleSearchParams]);
  /** 列表滚动到头部 */
  const handleScrollTop = () => {
    currentDataSource.length && tableRef.current?.scrollTo?.({ index: 0 });
  };
  // 表格初始化
  const initDable = () => {
    const params =
      pagination === false ? false : { ...initPagination, ...pagination, };
    setListParams({});
    setPaginationParams(params);
    fetch(allDefauleSearchParams, params, true, true);
    onReset?.(allDefauleSearchParams);
  };
  /** 改变加载状态 */
  const changeLoadingStatus = (val: boolean) => {
    setLoading(val);
  };
  /** 更新dataSource的值 */
  const updateDataSource = (newDataSource: any[]) => {
    setCurrentDataSource(newDataSource);
  };
  // 更新分页参数
  const updatePagination = (params: PaginationProps) => {
    setPaginationParams({
      ...paginationParams,
      ...params,
    });
    tableRef.current?.scrollTo?.({ index: 0 });
    fetch(listParams, { ...paginationParams, ...params }, false, true);
  };
  // 更新列表搜索参数
  const updateListParams = (params: paramsType) => {
    if (paginationParams && paginationParams.current !== 1) {
      setPaginationParams({
        ...paginationParams,
        current: 1,
      });
    }
    setListParams({ ...params });
    handleScrollTop();
    fetch({ ...params }, { ...paginationParams, current: 1 }, true);
  };
  /** 获取列表参数 */
  const getTableParams = async (): Promise<ListParamsProps> => {
    let searchParams = filterObjEmptyValue({ ...allDefauleSearchParams, ...listParams});
    if (associateSelectNames) {
      if (typeof associateSelectNames === 'string') {
        searchParams = formatAssociateParams(searchParams, associateSelectNames);
      }
      if (Array.isArray(associateSelectNames) && associateSelectNames.length) {
        associateSelectNames.forEach(associateSelectName => {
          searchParams = formatAssociateParams(searchParams, associateSelectName);
        });
      }
    }
    if (groupFields) {
      searchParams = formatGroupFields(groupFields, searchParams);
    }
    if (onBefore) {
      searchParams = await onBefore(searchParams);
    }
    return {
      searchParams: searchParams,
      paginationParams: paginationParams,
    };
  };
  // 刷新列表
  const refreshList = async (addParams?: IObject) => {
    const params = {...listParams, ...addParams};
    if (onBeforeRefresh) {
      !loading && setLoading(true);
      await onBeforeRefresh(params);
    }
    fetch(params, paginationParams, true);
    onRefresh?.(params, paginationParams);
  };
  // 重置分页
  const resetPagination = () => {
    if (pagination) {
      const params = { ...paginationParams, current: 1 };
      setPaginationParams(params);
      if (listApi) {
        fetch(listParams, params, false, true);
      }
    }
  };
  // 重置表格
  const resetTable = (type: 'pagination' | 'search' | 'all', addParams?: IObject) => {
    const params =
      pagination === false ? false : { ...initPagination, ...pagination };
    switch (type) {
      case 'pagination':
        fetch({...listParams, ...addParams}, params, false, true);
        break;
      case 'search':
        fetch({...addParams}, paginationParams, true);
        break;
      case 'all':
        fetch({...addParams}, params, true, true);
        break;
      default:
        break;
    }
  };
  // 获取列表数据
  const fetch = async (
    searchParams?: paramsType,
    pageParams?: PaginationProps | false,
    isSearchChange?: Boolean,
    isPaginationChange?: Boolean,
  ) => {
    if (hideContainer) return;
    if (fetchTimer.current) clearTimeout(fetchTimer.current);
    fetchTimer.current = setTimeout(async () => {
      if (!listApi && dataSource && !isMainSubTable && !isMergeTable && pageParams) {
        if (!pageParams) {
          setCurrentDataSource(dataSource);
          return;
        }
        const { current = 1, pageSize = 20 } = pageParams;
        const startNum = pageSize * (current - 1);
        const endNum = pageSize * current;
        setCurrentDataSource(allDataSource.current.slice(startNum, endNum));
        return;
      }
      if (!listApi) return;
      let params: any = {};
      let beforeParams: any = filterObjEmptyValue({
        // ...allDefauleSearchParams,
        ...defauleSearchParams,
        ...searchParams,
      });
      if (associateSelectNames) {
        if (typeof associateSelectNames === 'string') {
          beforeParams = formatAssociateParams(beforeParams, associateSelectNames);
        }
        if (Array.isArray(associateSelectNames) && associateSelectNames.length) {
          associateSelectNames.forEach(associateSelectName => {
            beforeParams = formatAssociateParams(beforeParams, associateSelectName);
          });
        }
      }
      if (groupFields) {
        beforeParams = formatGroupFields(groupFields, beforeParams);
      }
      if (onBefore) {
        beforeParams = await onBefore(beforeParams);
        if (beforeParams === false) {
          setListTotal(0);
          setPaginationParams(initPagination);
          setCurrentDataSource([]);
          return;
        };
      }
      if (isSearchChange) onSearchChange?.(beforeParams);
      if (isPaginationChange) onPaginationChange?.(pageParams);
      params = filterObjEmptyValue(beforeParams);
      if (pageParams && pagination !== false) {
        params.page = pageParams.current;
        params.size = pageParams.pageSize;
      }
      !loading && setLoading(true);
      fetchCount.current = fetchCount.current + 1
      const currentCount = fetchCount.current;
      const listData = await listApi(params);
      if (currentCount !== fetchCount.current) return;
      totalRef.current?.reload?.(beforeParams);
      setLoading(false);
      onFetchComplete?.(listData, pageParams);
      if (listData.code === SUCCESS_STATUS_CODE) {
        if (Array.isArray(formItems) && !!formItems.length) {
          setListParams(searchParams || {});
        }
        if (pagination !== false) {
          setListTotal(totalKey ? Number(listData.data[totalKey]) : 1);
          setPaginationParams(pageParams || initPagination);
        }
        const data = dataKey ? listData.data?.[dataKey] || [] : listData.data;
        const newDataSource = postData ? postData(data, params) : data;
        onFinal?.(newDataSource, pageParams);
        setCurrentDataSource(newDataSource);
      } else {
        setListTotal(0);
        setPaginationParams(initPagination);
        setCurrentDataSource([]);
      }
    }, delay);
  };
  const handleMenuClick = (
    key: string | number,
    rowData: any,
    index: number,
  ) => {
    onClickOperation && onClickOperation(key, rowData, index);
  };
  const handleSuccess = (
    value: string,
    valueKey: string,
    data: any,
    recordKey: any,
  ) => {
    const dataSource = actionRef?.current?.getDataSource();
    dataSource &&
      setCurrentDataSource(
        dataSource.map(item => {
          const itemKey = getRowKey(item, rowKey);
          if (itemKey === recordKey) {
            return {
              ...item,
              [valueKey]: value,
              ...data,
            };
          }
          return item;
        }),
      );
    onEditSuccess?.();
  };
  // 列
  const formatColumns = (
    columnsProp: columnsProps[],
    operationProp?: operationType,
  ) => {
    const mergeProps = { props: { colSpan: 0 } };
    // @ts-ignore
    let tableColumns: columnsProps[] = columnsProp.map((column, columnIndex) => {
      const {
        renderType,
        dataIndex,
        editable,
        children,
        ellipsisLine,
        popoverProps,
        moneyProps = {},
        copyLine,
        editSelect,
        title,
        timeProps,
        tooltip,
        align,
        isLocal,
        render,
        routeLinkProps,
        dataStatusProps,
        productInfoProps,
        skuInfoListProps,
        booleanProps,
      } = column;
      const baseColumnsProps: columnsProps = ellipsisLine
        ? {}
        : {
            ellipsis: {
              showTitle: false,
            },
          };
      if (resizable) {
        baseColumnsProps.onHeaderCell = (column: columnsProps) => ({
          width: column.width,
          onResize: handleResize(columnIndex) as React.ReactEventHandler<any>,
        })
      }
      if (editable) {
        return {
          width: 400,
          ...baseColumnsProps,
          ...column,
          title: setColumnTitle(title, tooltip, align),
          render: (_, record, key) => {
            const columnValue = getColumnValue(record, dataIndex);
            const searchName =
              typeof dataIndex === 'string' ? dataIndex : editable.searchName;
            if (record.isMainTable || record.isChildrenHidden !== undefined) {
              return render?.(columnValue, record, key) ?? mergeProps;
            }
            if (!searchName) return columnValue;
            return (
              <EdiTable
                record={record}
                text={columnValue === undefined ? undefined : columnValue}
                searchName={searchName}
                onSuccess={(value: string, data: any) =>
                  handleSuccess(
                    value,
                    searchName,
                    data,
                    getRowKey(record, rowKey),
                  )
                }
                {...editable}
              />
            );
          },
        };
      }
      if (Array.isArray(dataIndex)) {
        return {
          ...baseColumnsProps,
          ...column,
          title: setColumnTitle(title, tooltip, align),
          render: (val: any, record: any, key: number) => {
            if (record.isTotal)
              return render?.(val, record, key) ?? TABLE_COLUMN_EMPTY;
            if (record.isMainTable || record.isChildrenHidden !== undefined) {
              return render?.(val, record, key) ?? mergeProps;
            }
            if (!isMainSubTable && render) return render(val, record, key);
            return (
              <>
                {dataIndex.map((item, k) => {
                  const columnValue = getColumnValue(record, item);
                  if (
                    renderType === 'copy' &&
                    (!copyLine || copyLine.includes(k))
                  ) {
                    return <CopyText text={columnValue} isBlock key={item}/>;
                  }
                  return (
                    <ProTooltip
                      key={item}
                      placement="topLeft"
                      title={columnValue}
                      mouseEnterDelay={1}
                    >
                      <div className="ant-table-cell-ellipsis">
                        {filterEmptyValue(columnValue)}
                      </div>
                    </ProTooltip>
                  );
                })}
              </>
            );
          },
        };
      }
      switch (renderType) {
        case 'numberMark':
          return {
            ...baseColumnsProps,
            align: 'right',
            ...column,
            title: setColumnTitle(title, tooltip, align),
            render: (_: any, record: any, key: number) => {
              const columnValue = getColumnValue(record, dataIndex);
              if (record.isTotal)
                return render?.(columnValue, record, key) ?? TABLE_COLUMN_EMPTY;
              if (record.isMainTable || record.isChildrenHidden !== undefined) {
                return render?.(columnValue, record, key) ?? mergeProps;
              }
              if (columnValue > 0) return (<span className='text-success'>{columnValue}</span>);
              if (columnValue < 0) return (<span className='text-danger'>{columnValue}</span>);
              if (columnValue === undefined || columnValue === '' || columnValue === null) {
                return TABLE_COLUMN_EMPTY;
              }
              return columnValue;
            },
          };
        case 'skuInfoList':
          const skuInfoListWidth = Number(column.width || 580);
          return {
            ...baseColumnsProps,
            ...column,
            width: skuInfoListWidth + 10,
            title: setColumnTitle(title, tooltip, align),
            render: (_: string, record: any, key: number) => {
              const columnValue = getColumnValue(record, dataIndex);
              if (record.isTotal)
                return render?.(columnValue, record, key) ?? TABLE_COLUMN_EMPTY;
              if (record.isMainTable || record.isChildrenHidden !== undefined) {
                return render?.(columnValue, record, key) ?? mergeProps;
              }
              return (
                <SkuInfoList
                  data={columnValue}
                  width={skuInfoListWidth}
                  {...skuInfoListConfig}
                  {...skuInfoListProps}
                />
              );
            },
          };
        case 'productInfo':
          return {
            ...baseColumnsProps,
            ...column,
            title: setColumnTitle(title, tooltip, align),
            render: (_: string, record: any, key: number) => {
              const columnValue = getColumnValue(record, dataIndex);
              if (record.isTotal)
                return render?.(columnValue, record, key) ?? TABLE_COLUMN_EMPTY;
              if (record.isMainTable || record.isChildrenHidden !== undefined) {
                return render?.(columnValue, record, key) ?? mergeProps;
              }
              if (!productInfoProps) return TABLE_COLUMN_EMPTY;
              const {
                image: goodInfoColumnImage,
                showImage: goodInfoColumnShowImage,
                titlePrefix: goodInfoColumnTitlePrefix,
                title: goodInfoColumnTitle,
                copyTitle: goodInfoColumnCopyTitle,
                options: goodInfoColumnOptions,
                width: goodInfoColumnWidth, 
                className: goodInfoColumnClassName,
                style: goodInfoColumnStyle,
              } = productInfoProps;
              return (
                <GoodInfoColumn
                  image={typeof goodInfoColumnImage === 'function' ? goodInfoColumnImage(columnValue, record) : goodInfoColumnImage}
                  showImage={typeof goodInfoColumnShowImage === 'function' ? goodInfoColumnShowImage(columnValue, record) : goodInfoColumnShowImage}
                  titlePrefix={typeof goodInfoColumnTitlePrefix === 'function' ? goodInfoColumnTitlePrefix(columnValue, record) : goodInfoColumnTitlePrefix}
                  title={typeof goodInfoColumnTitle === 'function' ? goodInfoColumnTitle(columnValue, record) : goodInfoColumnTitle}
                  copyTitle={typeof goodInfoColumnCopyTitle === 'function' ? goodInfoColumnCopyTitle(columnValue, record) : goodInfoColumnCopyTitle}
                  options={typeof goodInfoColumnOptions === 'function' ? goodInfoColumnOptions(columnValue, record) : goodInfoColumnOptions}
                  style={typeof goodInfoColumnStyle === 'function' ? goodInfoColumnStyle(columnValue, record) : goodInfoColumnStyle}
                  className={typeof goodInfoColumnClassName === 'function' ? goodInfoColumnClassName(columnValue, record) : goodInfoColumnClassName}
                  width={typeof goodInfoColumnWidth === 'function' ? goodInfoColumnWidth(columnValue, record) : goodInfoColumnWidth}
                />
              );
            },
          };
        case 'productInfoList':
          return {
            ...baseColumnsProps,
            ...column,
            title: setColumnTitle(title, tooltip, align),
            render: (_: string, record: any, key: number) => {
              const columnValue = getColumnValue(record, dataIndex);
              if (record.isTotal)
                return render?.(columnValue, record, key) ?? TABLE_COLUMN_EMPTY;
              if (record.isMainTable || record.isChildrenHidden !== undefined) {
                return render?.(columnValue, record, key) ?? mergeProps;
              }
              if (!productInfoProps) return TABLE_COLUMN_EMPTY;
              if (Array.isArray(columnValue)) {
                return (
                  <div className='flex-column-start'>
                    {
                      columnValue.map((item, itemIndex) => {
                        const {
                          image: goodInfoColumnImage,
                          showImage: goodInfoColumnShowImage,
                          titlePrefix: goodInfoColumnTitlePrefix,
                          title: goodInfoColumnTitle,
                          copyTitle: goodInfoColumnCopyTitle,
                          options: goodInfoColumnOptions,
                          width: goodInfoColumnWidth, 
                          className: goodInfoColumnClassName,
                          style: goodInfoColumnStyle,
                        } = productInfoProps;
                        return (
                          <div key={itemIndex} style={{ paddingBottom: itemIndex !== columnValue.length - 1 ? 15 : 0}}>
                            <GoodInfoColumn
                              image={typeof goodInfoColumnImage === 'function' ? goodInfoColumnImage(item, record) : goodInfoColumnImage}
                              showImage={typeof goodInfoColumnShowImage === 'function' ? goodInfoColumnShowImage(item, record) : goodInfoColumnShowImage}
                              titlePrefix={typeof goodInfoColumnTitlePrefix === 'function' ? goodInfoColumnTitlePrefix(item, record) : goodInfoColumnTitlePrefix}
                              title={typeof goodInfoColumnTitle === 'function' ? goodInfoColumnTitle(item, record) : goodInfoColumnTitle}
                              copyTitle={typeof goodInfoColumnCopyTitle === 'function' ? goodInfoColumnCopyTitle(item, record) : goodInfoColumnCopyTitle}
                              options={typeof goodInfoColumnOptions === 'function' ? goodInfoColumnOptions(item, record) : goodInfoColumnOptions}
                              style={typeof goodInfoColumnStyle === 'function' ? goodInfoColumnStyle(item, record) : goodInfoColumnStyle}
                              className={typeof goodInfoColumnClassName === 'function' ? goodInfoColumnClassName(item, record) : goodInfoColumnClassName}
                              width={typeof goodInfoColumnWidth === 'function' ? goodInfoColumnWidth(item, record) : goodInfoColumnWidth}
                            />
                          </div>
                        );
                      })
                    }
                  </div>
                );
              }
            },
          };
        case 'time':
          if (!timeProps) return TABLE_COLUMN_EMPTY;
          const {
            zoneTypeKey,
            zoneOption,
            initialValue,
            render: timeRender,
          } = timeProps;
          const allListParams = { ...allDefauleSearchParams, ...listParams };
          const zoneTypeValue =
            dateType ||
            allListParams[zoneTypeKey] ||
            initialValue ||
            TIME_ZONE_OPTIONS[0].value;
          const zoneTypeOption = zoneOption ? zoneOption : [];
          const findItem = zoneTypeOption.find(v => v.value === zoneTypeValue);
          const zoneTypeLabel = findItem?.label ? `(${findItem?.label})` : '';
          return {
            ...baseColumnsProps,
            ...column,
            title: setColumnTitle(<span>{title || ''}{zoneTypeLabel}</span>, tooltip, align),
            render: (_: string, record: any, key: number) => {
              const columnValue = getColumnValue(record, dataIndex);
              if (record.isTotal)
                return render?.(columnValue, record, key) ?? TABLE_COLUMN_EMPTY;
              if (record.isMainTable || record.isChildrenHidden !== undefined) {
                return render?.(columnValue, record, key) ?? mergeProps;
              }
              if (findItem) {
                const timeDataIndex = findItem.dataIndex;
                if (findItem.render) {
                  return findItem.render(
                    timeDataIndex ? record[timeDataIndex] || '' : record,
                    record,
                    key,
                  );
                }
                if (timeRender) {
                  return timeRender(
                    timeDataIndex ? record[timeDataIndex] || '' : record,
                    record,
                    key,
                  );
                }
                if (timeDataIndex) return record?.[timeDataIndex] || TABLE_COLUMN_EMPTY;
                return TABLE_COLUMN_EMPTY;
              }
              return columnValue;
            },
          };
        case 'select':
          return {
            ...baseColumnsProps,
            ...column,
            title: setColumnTitle(title, tooltip, align),
            render: (_: string, record: any, key: number) => {
              const columnValue = getColumnValue(record, dataIndex);
              if (record.isTotal)
                return render?.(columnValue, record, key) ?? TABLE_COLUMN_EMPTY;
              if (record.isMainTable || record.isChildrenHidden !== undefined) {
                return render?.(columnValue, record, key) ?? mergeProps;
              }
              if (editSelect) {
                const {
                  defaultParams,
                  labelKey,
                  valueKey,
                  onSuccess,
                  selectTableProps,
                  disabled,
                  ...restEditSelect
                } = editSelect;
                const { otherParams } = selectTableProps;
                let selectTableParams =
                  otherParams && typeof otherParams === 'function'
                    ? otherParams(columnValue, record, key)
                    : otherParams;
                let newDefaultParams =
                  typeof defaultParams === 'function'
                    ? defaultParams(record, key)
                    : defaultParams;
                if (record.isMainTable || record.isChildrenHidden !== undefined) {
                  return render?.(columnValue, record, key) ?? mergeProps;
                }    
                return (
                  <EditSelect
                    defaultParams={newDefaultParams}
                    onSuccess={(data: any) => {
                      actionRef?.current?.reload();
                      onSuccess?.(data);
                    }}
                    value={valueKey ? record?.[valueKey] : undefined}
                    label={labelKey ? record?.[labelKey] : undefined}
                    selectTableProps={{
                      ...selectTableProps,
                      otherParams: selectTableParams,
                    }}
                    disabled={typeof disabled === 'function' ? disabled(columnValue, record, key) : disabled}
                    {...restEditSelect}
                  />
                );
              }
              return TABLE_COLUMN_EMPTY;
            },
          };
        case 'image':
          return {
            ...baseColumnsProps,
            ...column,
            className: 'table-image-column',
            title: setColumnTitle(title, tooltip, align),
            render: (_: string, record: any, key: number) => {
              const columnValue = getColumnValue(record, dataIndex);
              if (record.isTotal)
                return render?.(columnValue, record, key) ?? TABLE_COLUMN_EMPTY;
              if (record.isMainTable || record.isChildrenHidden !== undefined) {
                return render?.(columnValue, record, key) ?? mergeProps;
              }
              return (
                <div className={classNames({
                  'flex-center-start': align === 'left',
                  'flex-center-center': align === 'center',
                  'flex-center-end': align === 'right',
                })}>
                  <ProImage
                    isLocal={isLocal}
                    className="table-column-image"
                    src={columnValue}
                  />
                </div>
              );
            },
          };
        case 'images':
          return {
            ...baseColumnsProps,
            ...column,
            title: setColumnTitle(title, tooltip, align),
            render: (_: string, record: any, key: number) => {
              const columnValue = getColumnValue(record, dataIndex);
              if (record.isTotal)
                return render?.(columnValue, record, key) ?? TABLE_COLUMN_EMPTY;
              if (record.isMainTable || record.isChildrenHidden !== undefined) {
                return render?.(columnValue, record, key) ?? mergeProps;
              }
              if (Array.isArray(columnValue)) return <Images images={columnValue} />;
              return null;
            },
          };
        case 'boolean':
          return {
            ...baseColumnsProps,
            ...column,
            title: setColumnTitle(title, tooltip, align),
            render: (_: any, record: any, key: number) => {
              const columnValue = getColumnValue(record, dataIndex);
              if (record.isTotal)
                return render?.(columnValue, record, key) ?? TABLE_COLUMN_EMPTY;
              if (record.isMainTable || record.isChildrenHidden !== undefined) {
                return render?.(columnValue, record, key) ?? mergeProps;
              }
              if (columnValue === true) return booleanProps?.checkedChildren || '是';
              if (columnValue === false) return booleanProps?.unCheckedChildren || '否';
              return TABLE_COLUMN_EMPTY;
            },
          };
        case 'copy':
          return {
            ...baseColumnsProps,
            ...column,
            title: setColumnTitle(title, tooltip, align),
            render: (_: any, record: any, key: number) => {
              const columnValue = getColumnValue(record, dataIndex);
              if (record.isTotal)
                return render?.(columnValue, record, key) ?? TABLE_COLUMN_EMPTY;
              if (record.isMainTable || record.isChildrenHidden !== undefined) {
                return render?.(columnValue, record, key) ?? mergeProps;
              }
              if (!isMainSubTable && render) return render(columnValue, record, key);
              return <CopyText text={columnValue} align={align} ellipsisLine={ellipsisLine} />;
            },
          };
        case 'routeLink':
          return {
            ...baseColumnsProps,
            ...column,
            title: setColumnTitle(title, tooltip, align),
            render: (_: any, record: any, key: number) => {
              const columnValue = getColumnValue(record, dataIndex);
              if (record.isTotal)
                return render?.(columnValue, record, key) ?? TABLE_COLUMN_EMPTY;
              if (record.isMainTable || record.isChildrenHidden !== undefined) {
                return render?.(columnValue, record, key) ?? mergeProps;
              }
              if (!isMainSubTable && render) return render(columnValue, record, key);
              const routeLinkQuery = routeLinkProps?.query;
              const routeLinkState = routeLinkProps?.state;
              return (
                <RouteLine
                  text={columnValue}
                  {...routeLinkProps}
                  query={typeof routeLinkQuery === 'function' ? routeLinkQuery(record) : routeLinkQuery}
                  state={typeof routeLinkState === 'function' ? routeLinkState(record) : routeLinkState}
                />
              );
            },
          };
        case 'money':
          return {
            ...baseColumnsProps,
            align: 'right',
            ...column,
            title: setColumnTitle(title, tooltip, align),
            render: (_: any, record: any, key: number) => {
              /** 主子表小计行显示问题注释 */
              // if (record.isTotal) {
              //   return render?.(vals, record, key) ?? TABLE_COLUMN_EMPTY;
              // }
              const columnValue = getColumnValue(record, dataIndex);
              if (record.isMainTable || record.isChildrenHidden !== undefined) {
                return render?.(columnValue, record, key) ?? mergeProps;
              }
              /** 子表显示问题注释 */
              // if (render) return render(vals, record, key);
              const {
                color,
                fontWeight,
                fontSize,
                ...restMoneyProps
              } = moneyProps;
              const monryString = moneyFormat(columnValue, restMoneyProps);
              return (
                <ProTooltip title={monryString}>
                  <span style={{ color, fontWeight, fontSize }}>
                    {monryString}
                  </span>
                </ProTooltip>
              );
            },
          };
        case 'popoverList':
          return {
            ...baseColumnsProps,
            ...column,
            title: setColumnTitle(title, tooltip, align),
            render: (_: any, record: any, key: number) => {
              const columnValue = getColumnValue(record, dataIndex);
              if (record.isTotal)
                return render?.(columnValue, record, key) ?? TABLE_COLUMN_EMPTY;
              if (record.isMainTable || record.isChildrenHidden !== undefined) {
                return render?.(columnValue, record, key) ?? mergeProps;
              }
              if (popoverProps) {
                const { dataSource: popoverDataSource, title: popoverTitle, ...restPopoverProps } = popoverProps;
                return (
                  <PopoverTable
                    dataSource={popoverDataSource ? (typeof popoverDataSource === 'function' ? popoverDataSource(record) : popoverDataSource) : columnValue}
                    // @ts-ignore
                    title={typeof popoverTitle === 'function' ? popoverTitle(record) : popoverTitle}
                    {...restPopoverProps}
                  />
                )
              }
              return TABLE_COLUMN_EMPTY;
            },
          };
        case 'dataStatus':
          return {
            ...baseColumnsProps,
            ...column,
            title: setColumnTitle(title, tooltip, align),
            render: (_: any, record: any, key: number) => {
              const columnValue = getColumnValue(record, dataIndex);
              if (record.isMainTable || record.isChildrenHidden !== undefined) {
                return render?.(columnValue, record, key) ?? mergeProps;
              }
              const newDataStatusProps = {...dataStatusProps, value: columnValue};
              return (
                <DataStatus {...newDataStatusProps} />
              );
            },
          };
      }
      if (column.enumName && enumData) {
        const enumItem = enumData[`${column.enumName}`];
        if (!enumItem) {
          return {
            ...baseColumnsProps,
            ...column,
            title: setColumnTitle(title, tooltip, align),
            render: (_: any, record: any, key: number) => {
              const columnValue = getColumnValue(record, dataIndex);
              if (record.isTotal)
                return render?.(columnValue, record, key) ?? TABLE_COLUMN_EMPTY;
              if (record.isMainTable || record.isChildrenHidden !== undefined) {
                return render?.(columnValue, record, key) ?? mergeProps;
              }
              return TABLE_COLUMN_EMPTY;
            },
          };
        }
        return enumItem
          ? {
              ...baseColumnsProps,
              ...column,
              title: setColumnTitle(title, tooltip, align),
              render: (_: any, record: any, key: number) => {
                const columnValue = getColumnValue(record, dataIndex);
                if (
                  record.isMainTable ||
                  record.isChildrenHidden !== undefined
                ) {
                  return render?.(columnValue, record, key) ?? mergeProps;
                }
                const enumRender = renderEnumNames(columnValue, enumItem.items);
                return <ProTooltip title={enumRender}>{enumRender}</ProTooltip>;
              },
            }
          : { ...baseColumnsProps, ...column };
      }
      return {
        ...baseColumnsProps,
        ...column,
        title: setColumnTitle(title, tooltip, align),
        children: children ? formatColumns(children, false) : undefined,
        render: (_: any, record: any, key: number) => {
          const columnValue = getColumnValue(record, dataIndex);
          if (record.isMainTable) {
            return column?.render?.(columnValue, record, key);
          }
          if (columnValue === undefined || columnValue === '' || columnValue === null)
            return render ? render(columnValue, record, key) : TABLE_COLUMN_EMPTY;
          if (render) return render(columnValue, record, key, paginationParams);
          if (typeof columnValue === 'string' || typeof columnValue === 'number') {
            if (ellipsisLine) {
              return (
                <ProTooltip placement="topLeft" title={columnValue}>
                  <div className={`yb-line-ellipsis-${ellipsisLine}`}>
                    {columnValue}
                  </div>
                </ProTooltip>
              );
            }
            return (
              <ProTooltip placement="topLeft" title={columnValue}>
                {columnValue}
              </ProTooltip>
            );
          }
        },
      };
    });
    if (operationProp !== false) {
      const baseOperationProp: columnsProps = {
        title: '操作',
        width: 130,
        fixed: 'right',
        align: 'center',
      };
      if (typeof operationProp === 'string') {
        const btnLists = CommonlyTypeEnum[operationProp];
        if (!btnLists) return tableColumns;
        tableColumns.push({
          ...baseOperationProp,
          render: (text: string, rowData: any, index: number) => {
            return (
              <ButtonGroup
                isTableOperation
                btnLists={[btnLists as IBtnList]}
                footerList={operationFooterList}
                onClick={key => handleMenuClick(key, rowData, index)}
              />
            );
          },
        });
        return tableColumns;
      }
      if (Array.isArray(operationProp)) {
        tableColumns.push({
          ...baseOperationProp,
          render: (text: string, rowData: any, index: number) => {
            const btnLists = operationProp.map(v => {
              if (typeof v === 'object') return v;
              return CommonlyTypeEnum[v];
            });
            return (
              <ButtonGroup
                isTableOperation
                btnLists={btnLists as IBtnList[]}
                footerList={operationFooterList}
                onClick={key => handleMenuClick(key, rowData, index)}
              />
            );
          },
        });
        return tableColumns;
      }
      if (typeof operationProp === 'object') {
        const { render, customRender, ...resOperationProp } = operationProp;
        if (Array.isArray(render)) {
          tableColumns.push({
            ...baseOperationProp,
            ...resOperationProp,
            render: (text: string, rowData: any, index: number) => {
              return (
                <ButtonGroup
                  isTableOperation
                  btnLists={render as IBtnList[]}
                  footerList={operationFooterList}
                  onClick={key => handleMenuClick(key, rowData, index)}
                />
              );
            },
          });
        } else if (customRender) {
          tableColumns.push({
            ...baseOperationProp,
            ...resOperationProp,
            render: (text: string, rowData: any, index: number) => {
              const currentBtnLists = customRender(text, rowData, index);
              const newBtnLists: IBtnList[] = currentBtnLists.map(list => {
                if (typeof list === 'string') {
                  return CommonlyTypeEnum[list];
                }
                return list;
              });
              return (
                <ButtonGroup
                  isTableOperation
                  btnLists={newBtnLists}
                  footerList={operationFooterList}
                  onClick={key => handleMenuClick(key, rowData, index)}
                />
              );
            },
          });
        } else {
          tableColumns.push({
            ...baseOperationProp,
            ...resOperationProp,
            render,
          });
        }
      }
    }
    return tableColumns;
  };
  const [tableColumns, setTableColumns] = useState<columnsProps[]>(
    formatColumns(columns, operation),
  );
  useEffect(() => {
    const newColumns = formatColumns(columns, operation);
    setTableColumns(newColumns);
  }, [columns, operation, loading]);

  const handleResize = (index: number) =>
    (_: React.SyntheticEvent<Element>, { size }: ResizeCallbackData) => {
      setTableColumns(prevColumns => {
        const newColumns = [...prevColumns];
        newColumns[index] = {
          ...newColumns[index],
          width: size.width,
        };
        return newColumns;
      });
    };
  const onChangeColumns = (checkedValue: any[], newColumns?: columnsProps[]) => {
    if (newColumns) {
      setTableColumns(newColumns)
      return;
    }
    setTableColumns(prevColumns => {
      const newColumns = prevColumns.map((v, index) => ({
        ...v,
        isHide: !checkedValue.includes(index),
      }));
      return newColumns;
    });
  };
  return {
    loading,
    virtual: minVirtualTotal ? (listTotal >= minVirtualTotal ? true : false ) : false,
    currentDataSource,
    initDable,
    updateDataSource,
    updatePagination,
    updateListParams,
    refreshList,
    resetTable,
    onChangeColumns,
    getTableParams,
    changeLoadingStatus,
    resetPagination,
    // listParams: { ...allDefauleSearchParams, ...listParams},
    total: listTotal,
    columns: tableColumns, // formatColumns(columns, operation),
    pagination: paginationParams,
  };
}

export default useTableHook;
